import type {
  QuizAnswerId,
  QuizQuestionId,
  UserQuizId,
  UserQuizzesSection,
} from 'Store/profile/user.types';

import type { ChoiceId } from 'Components/ProfileElements/ChoiceSelector/types';

import type { Answer } from '../types';

type EditedQuizInfo = {
  answers: Answer[];
  quizId: UserQuizId;
  type: 'QUIZ_EDIT';
};
type RemovedQuizInfo = {
  quizId: UserQuizId;
  type: 'QUIZ_REMOVE';
};

/** @returns A string representation of the user's quizzes answers in the format expected for the DynamicUI.Edit analytics event. */
export function stringifyQuizzesAnswers(
  quizzes: UserQuizzesSection['quizzes'],
  modifiedQuizInfo: EditedQuizInfo | RemovedQuizInfo | null
): string {
  const answerIdsPerQuiz = getQuizzesAnswerIds(quizzes, modifiedQuizInfo);
  return answerIdsPerQuiz.map((answerIds) => `{${concatenateQuizAnswers(answerIds)}}`).join(';');
}

export function getAnswerId(questionId: QuizQuestionId, choiceId: ChoiceId): QuizAnswerId {
  return `${questionId}_${choiceId}`;
}

export function getAnswerIds(answers: Answer[]): QuizAnswerId[] {
  return answers.map(({ questionId, choiceId }) => getAnswerId(questionId, choiceId));
}

export function concatenateQuizAnswers(answerIds: QuizAnswerId[]): string {
  return answerIds.join(',');
}

function getQuizzesAnswerIds(
  quizzes: UserQuizzesSection['quizzes'],
  modifiedQuizInfo: EditedQuizInfo | RemovedQuizInfo | null
): QuizAnswerId[][] {
  if (quizzes.length === 0 && modifiedQuizInfo?.type === 'QUIZ_EDIT') {
    // NOTE: this case is not expected, as the user should have a quiz item for every quiz, even if they haven't taken it.
    return [getAnswerIds(modifiedQuizInfo.answers)];
  }

  return quizzes
    .map((quiz): QuizAnswerId[] => {
      if (modifiedQuizInfo?.type === 'QUIZ_REMOVE' && quiz.id === modifiedQuizInfo.quizId) {
        return [];
      } else if (modifiedQuizInfo?.type === 'QUIZ_EDIT' && quiz.id === modifiedQuizInfo.quizId) {
        return getAnswerIds(modifiedQuizInfo.answers);
      }
      return quiz.answer_details.map(({ answer_id }) => answer_id);
    })
    .filter((answerIds) => answerIds.length > 0);
}
